import type { StripeSubscription } from '@flintmint/core-shared/firestore/schemas';
import { formatCount, formatTypicalNotes } from '@flintmint/core-shared/utils';

const billingDateFormat = new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

/** "November 7, 2026", for a date row and wherever a design sentence keeps the year. */
export const formatBillingDate = (iso: string) => billingDateFormat.format(new Date(iso));

const billingDayMonthFormat = new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' });

/** "November 7", for a date inside a sentence where the design leaves out the year. */
export const formatBillingDayMonth = (iso: string) => billingDayMonthFormat.format(new Date(iso));

export const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The same day `months` calendar months later, or that month's last day when it is shorter (January 31 to February
 * 28 or 29), as Stripe bills a subscription whose anchor day the month lacks.
 */
export const addCalendarMonths = (date: Date, months: number) => {
  const next = new Date(date);
  next.setDate(1);
  next.setMonth(next.getMonth() + months);
  const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(date.getDate(), lastDay));
  return next;
};

/** "$75", "$7.50". */
export const formatPrice = (cents: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);

/** "$75.00", "$7.50": always with cents, as the review screen shows a charge. */
export const formatPriceWithCents = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(cents / 100);

/** "1,000 / 2,500 Joules", with " · <detail>" for each defined detail. */
export const formatJoules = (left: number, total: number, ...details: (string | undefined)[]) =>
  [`${formatCount(left)} / ${formatCount(total)} Joules`, ...details].filter(Boolean).join(' · ');

/** "Monthly Energy: 2,500 Joules · About 125 typical notes"; without the estimate while the settings are not loaded. */
export const formatMonthlyEnergyRow = (monthlyCredits: number, creditsPerTypicalNote: number | undefined) => {
  const typicalNotes = formatTypicalNotes(monthlyCredits, creditsPerTypicalNote);
  return [`Monthly Energy: ${formatCount(monthlyCredits)} Joules`, typicalNotes].filter(Boolean).join(' · ');
};

export const intervalLabels: Record<StripeSubscription['interval'], string> = { month: 'Monthly', year: 'Annual' };
